import { useEffect, useRef, useState } from "react";
import { X, Send, ShieldAlert, UserRound, Stethoscope, Lock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { useAuth } from "@/context/AuthContext";
import { useMessaging } from "@/context/MessagingContext";

const MAX_CHARS = 2000;
const MAX_MSGS_PER_ROLE = 10;

function nonSpaceLen(s) {
  let c = 0;
  for (const ch of s || "") if (!/\s/.test(ch)) c++;
  return c;
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function fmtAbs(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

/**
 * Private patient<->doctor notes for a single appointment.
 * Strictly scoped: admin cannot read. Hard limits: 2000 non-space chars / 10 msgs per role.
 * Accessed only from the Appointments list row's "Message" button.
 */
export default function ApptNotesModal({ appt, onClose, role, accent = "#185FA5" }) {
  const { user } = useAuth();
  const { refreshSummary } = useMessaging();
  const [data, setData] = useState({ notes: [], counts: null, other_party: "", other_name: "", window: null });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/appointments/${appt.id}/private-notes`);
      setData(r.data);
      // mark notes from other party as read (best-effort) — only if channel is open
      if (r.data?.window?.is_open) {
        try {
          await api.post(`/appointments/${appt.id}/private-notes/read`);
          refreshSummary && refreshSummary();
        } catch { /* ignore */ }
      }
    } catch (e) { toast(formatApiError(e.response?.data?.detail) || "Could not load notes."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [appt?.id]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [data.notes.length]);

  const myCount = role === "patient" ? data.counts?.patient_count ?? 0 : data.counts?.doctor_count ?? 0;
  const myRemaining = role === "patient" ? data.counts?.patient_remaining ?? MAX_MSGS_PER_ROLE : data.counts?.doctor_remaining ?? MAX_MSGS_PER_ROLE;
  const otherCount = role === "patient" ? data.counts?.doctor_count ?? 0 : data.counts?.patient_count ?? 0;

  const window_ = data.window || { is_open: true, status: "open", reason: "" };
  const channelOpen = window_.is_open;

  const ncLen = nonSpaceLen(text);
  const canSend = channelOpen && !busy && text.trim() && ncLen <= MAX_CHARS && myRemaining > 0;
  const limitReached = channelOpen && myRemaining <= 0;

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      const r = await api.post(`/appointments/${appt.id}/private-notes`, { text });
      setData((d) => ({ ...d, notes: [...d.notes, r.data.note], counts: r.data.counts }));
      setText("");
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const counterColor = ncLen > MAX_CHARS ? "#B91C1C" : ncLen > MAX_CHARS * 0.9 ? "#B45309" : "#64748B";
  const mineIs = role; // "patient" or "doctor"
  const otherLabel = role === "patient" ? "Doctor" : "Patient";

  return (
    <div className="modal-bg" onClick={onClose} data-testid="appt-notes-modal-bg">
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720, width: "94%", padding: 0, display: "flex", flexDirection: "column", maxHeight: "90vh" }} data-testid="appt-notes-modal">
        {/* Header */}
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #E5EAF0", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Lock size={16} style={{ color: accent }} />
              <b style={{ color: "#042C53", fontSize: 16 }}>Private notes · {data.other_name || (role === "patient" ? "your doctor" : "your patient")}</b>
              {window_.status === "open" && (
                <span style={{ background: "#E9F7F1", color: "#1D9E75", padding: "2px 9px", borderRadius: 999, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" }} data-testid="appt-notes-status-pill">Open</span>
              )}
              {window_.status === "pre" && (
                <span style={{ background: "#EEF1F4", color: "#475569", padding: "2px 9px", borderRadius: 999, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" }} data-testid="appt-notes-status-pill">Opens later</span>
              )}
              {window_.status === "closed" && (
                <span style={{ background: "#FDECEC", color: "#7B1F1F", padding: "2px 9px", borderRadius: 999, fontSize: 10.5, fontWeight: 700, letterSpacing: 0.3, textTransform: "uppercase" }} data-testid="appt-notes-status-pill">Closed</span>
              )}
            </div>
            <small style={{ color: "#64748B" }}>
              {appt.date} · {appt.time} {appt.specialty ? `· ${appt.specialty}` : ""}
            </small>
          </div>
          <button className="btn btn-ghost" onClick={onClose} data-testid="appt-notes-close" aria-label="Close"><X size={18} /></button>
        </div>

        {/* Warning */}
        <div style={{ padding: "12px 22px", background: "#FFF8E6", borderBottom: "1px solid #FAE7B6", display: "flex", gap: 10, alignItems: "flex-start" }} data-testid="appt-notes-warning">
          <ShieldAlert size={18} style={{ color: "#B45309", flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: "#3F2D00", lineHeight: 1.5 }}>
            <b>Health information only.</b> Use this channel strictly to share clinical/health details with{" "}
            {role === "patient" ? "your doctor" : "your patient"} for this appointment.
            For any questions about the platform or its services, please raise a ticket through the{" "}
            <b>Communications</b> portal.
          </div>
        </div>

        {/* Counter bar */}
        <div style={{ padding: "10px 22px", borderBottom: "1px solid #E5EAF0", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, fontSize: 12.5, color: "#475569", background: "#FAFBFD" }} data-testid="appt-notes-counters">
          <span><b style={{ color: "#042C53" }}>You ({role}):</b> {myCount} / {MAX_MSGS_PER_ROLE} messages used</span>
          <span><b style={{ color: "#042C53" }}>{otherLabel}:</b> {otherCount} / {MAX_MSGS_PER_ROLE} messages used</span>
        </div>

        {/* Thread */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: "16px 22px", display: "flex", flexDirection: "column", gap: 10, background: "#F6F8FB", minHeight: 280 }} data-testid="appt-notes-thread">
          {loading && <p className="lead" style={{ textAlign: "center" }}>Loading…</p>}
          {!loading && data.notes.length === 0 && (
            <div style={{ textAlign: "center", color: "#64748B", padding: "32px 0" }}>
              <p style={{ marginBottom: 4 }}>No messages yet.</p>
              <small>Send a quick note below — your {role === "patient" ? "doctor" : "patient"} will be notified.</small>
            </div>
          )}
          {data.notes.map((n) => {
            const mine = n.sender_role === mineIs;
            const Icon = n.sender_role === "patient" ? UserRound : Stethoscope;
            return (
              <div key={n.id} style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }} data-testid={`appt-note-${n.id}`}>
                <div style={{
                  maxWidth: "78%",
                  background: mine ? accent : "#fff",
                  color: mine ? "#fff" : "#1F2937",
                  border: mine ? "none" : "1px solid #E5EAF0",
                  borderRadius: 14,
                  padding: "10px 14px",
                  boxShadow: mine ? "0 6px 14px -8px rgba(24,95,165,0.45)" : "0 1px 2px rgba(0,0,0,0.04)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, fontSize: 11, opacity: 0.85 }}>
                    <Icon size={12} />
                    <b>{mine ? "You" : (n.sender_name || (n.sender_role === "patient" ? "Patient" : "Doctor"))}</b>
                    <span>· {fmtTime(n.created_at)}</span>
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.5 }}>{n.text}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Composer */}
        <div style={{ borderTop: "1px solid #E5EAF0", padding: "14px 22px", background: "#fff" }}>
          {!channelOpen ? (
            <div
              style={{
                background: window_.status === "pre" ? "#EEF6FB" : "#FDECEC",
                border: `1px solid ${window_.status === "pre" ? "#C8DDF0" : "#F5C2C2"}`,
                color: window_.status === "pre" ? "#0F3D6E" : "#7B1F1F",
                padding: "12px 14px", borderRadius: 10, fontSize: 13, lineHeight: 1.5,
              }}
              data-testid={`appt-notes-window-${window_.status}`}
            >
              <b>{window_.status === "pre" ? "Channel opens later" : window_.status === "closed" ? "Channel closed" : "Channel unavailable"}</b>
              <div style={{ marginTop: 4 }}>
                {window_.reason || "This message channel is not currently open."}
                {window_.status === "pre" && window_.opens_at && (
                  <> Opens on <b>{fmtAbs(window_.opens_at)}</b>.</>
                )}
                {window_.status === "closed" && role === "patient" && (
                  <> To message this doctor again, please book a new appointment.</>
                )}
              </div>
            </div>
          ) : limitReached ? (
            <div style={{ background: "#FDECEC", border: "1px solid #F5C2C2", color: "#7B1F1F", padding: "10px 12px", borderRadius: 10, fontSize: 13 }} data-testid="appt-notes-limit-msg">
              {role === "patient"
                ? "You've used all 10 messages for this appointment. To continue messaging this doctor, please rebook an appointment."
                : "You've used all 10 replies for this appointment."}
            </div>
          ) : (
            <>
              <textarea
                className="in"
                rows={3}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKey}
                placeholder={`Write a brief health-related note to your ${role === "patient" ? "doctor" : "patient"}…`}
                disabled={busy}
                data-testid="appt-notes-input"
                style={{ resize: "vertical", fontFamily: "inherit", fontSize: 14 }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8, gap: 12, flexWrap: "wrap" }}>
                <small style={{ color: counterColor, fontWeight: 600 }} data-testid="appt-notes-char-count">
                  {ncLen} / {MAX_CHARS} characters (excluding spaces)
                </small>
                <button
                  className="btn btn-ink"
                  onClick={send}
                  disabled={!canSend}
                  data-testid="appt-notes-send"
                  style={{ background: canSend ? accent : "#94A3B8" }}
                >
                  <Send size={14} /> {busy ? "Sending…" : `Send (${myRemaining} left)`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
